import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Per-item detail PDF exports for food-cost module.
 * Each function generates a single-page PDF with all data for one item.
 * Opens in a new browser tab.
 */

// ── Shared helpers ────────────────────────────────────────────────────────

const MARGIN = 14;

// ── Arabic font loading (Amiri) ──────────────────────────────────────────

/** Cached base64 font data — loaded once, reused across PDF generations */
let amiriFontBase64 = null;

/**
 * Load the Amiri Arabic font and register it with a jsPDF document.
 * Font is fetched from public/fonts/ on first call, then cached in memory.
 */
async function loadArabicFont(doc) {
  if (!amiriFontBase64) {
    const response = await fetch("/fonts/Amiri-Regular.ttf");
    const buffer = await response.arrayBuffer();
    // Convert ArrayBuffer to base64 string for jsPDF
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    amiriFontBase64 = btoa(binary);
  }
  // Register the font with this document instance
  doc.addFileToVFS("Amiri-Regular.ttf", amiriFontBase64);
  doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
}

/** Check if a string contains Arabic characters */
function hasArabic(text) {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text || "");
}

/** Create an A4 jsPDF doc with Arabic font pre-loaded */
async function createDoc(landscape = false) {
  const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "mm", format: "a4" });
  await loadArabicFont(doc);
  return doc;
}

/** Add standard header: company name + title + date */
function addHeader(doc, title) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Pizza Felice LLC", MARGIN, MARGIN);
  doc.setFont("helvetica", "normal");
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  doc.text(dateStr, pageWidth - MARGIN, MARGIN, { align: "right" });

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(title, MARGIN, MARGIN + 10);
  doc.setFont("helvetica", "normal");
  return MARGIN + 16;
}

/** Add footer with page numbers, timestamp, and confidential watermark on all pages */
function addFooter(doc) {
  const totalPages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const now = new Date();
  const dateStr = now.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Diagonal "INTERNAL — CONFIDENTIAL" watermark
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.setFontSize(54);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    const cx = pageWidth / 2;
    const cy = pageHeight / 2;
    doc.text("INTERNAL \u2014 CONFIDENTIAL", cx, cy, { align: "center", angle: 35 });
    doc.restoreGraphicsState();

    // Page footer text
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on ${dateStr} at ${timeStr}`, MARGIN, pageHeight - 6);
    doc.text(`Page ${i} / ${totalPages}`, pageWidth - MARGIN, pageHeight - 6, { align: "right" });
  }
  doc.setTextColor(0, 0, 0);
}

/** Open PDF in new browser tab */
function openPdf(doc) {
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

/** Format number, return "-" if falsy */
function fmt(val, decimals = 2) {
  if (val === null || val === undefined || val === "") return "-";
  return Number(val).toFixed(decimals);
}

/** Print a label: value pair, return updated y. Switches to Amiri font for Arabic values. */
function printField(doc, label, value, y) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  // Check page break
  if (y + 6 > pageHeight - 14) { doc.addPage(); y = MARGIN; }
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(`${label}:`, MARGIN, y);
  doc.setFont("helvetica", "normal");
  const labelWidth = doc.getTextWidth(`${label}: `);
  const valStr = String(value ?? "-");
  // Use Amiri font for Arabic text, Helvetica for Latin
  if (hasArabic(valStr)) {
    doc.setFont("Amiri", "normal");
    doc.text(valStr, MARGIN + labelWidth + 1, y);
    doc.setFont("helvetica", "normal");
  } else {
    doc.text(valStr, MARGIN + labelWidth + 1, y);
  }
  return y + 5;
}

/** Print a section title, return updated y */
function printSection(doc, title, y) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + 10 > pageHeight - 14) { doc.addPage(); y = MARGIN; }
  y += 3;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(title, MARGIN, y);
  doc.setFont("helvetica", "normal");
  return y + 6;
}

// ── Ingredient Detail PDF ─────────────────────────────────────────────────

/**
 * Generate a detail PDF for a single ingredient.
 * @param {Object} item - Ingredient object with all fields
 */
export async function exportIngredientDetailPdf(item) {
  if (!item) return;

  const doc = await createDoc();
  let y = addHeader(doc, item.name || "Ingredient");

  // Fields
  y = printField(doc, "Brand", item.brand_name, y);
  y = printField(doc, "Supplier", item.supplier_name, y);
  y = printField(doc, "Category", item.category_name, y);
  y = printField(doc, "Unit", item.unit, y);
  y = printField(doc, "Price per Unit", fmt(item.price_per_unit, 4), y);
  if (item.package_price) y = printField(doc, "Package Price", fmt(item.package_price), y);
  if (item.package_size) y = printField(doc, "Package Size", fmt(item.package_size), y);
  if (item.sku) y = printField(doc, "SKU", item.sku, y);
  if (item.notes) y = printField(doc, "Notes", item.notes, y);
  y = printField(doc, "Active", item.is_active ? "Yes" : "No", y);

  addFooter(doc);
  openPdf(doc);
}

// ── Ingredients List PDF ──────────────────────────────────────────────────

/**
 * Generate a PDF table listing all ingredients.
 * @param {Array} items - Array of ingredient objects
 */
export async function exportIngredientsListPdf(items) {
  if (!items?.length) return;

  const doc = await createDoc(true);
  let y = addHeader(doc, "Ingredients");

  const head = [["Name", "Brand", "Supplier", "Category", "Unit", "Price/Unit", "SKU"]];
  const body = items.map((i) => [
    i.name || "",
    i.brand_name || "-",
    i.supplier_name || "-",
    i.category_name || "-",
    i.unit || "",
    fmt(i.price_per_unit, 4),
    i.sku || "-",
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head,
    body,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [97, 97, 97], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    columnStyles: { 5: { halign: "right" } },
  });

  addFooter(doc);
  openPdf(doc);
}

// ── Packaging List PDF ───────────────────────────────────────────────────

/**
 * Generate a PDF table listing all packaging items.
 * @param {Array} items - Array of packaging objects
 */
export async function exportPackagingListPdf(items) {
  if (!items?.length) return;

  const doc = await createDoc(true);
  let y = addHeader(doc, "Packaging");

  const head = [["Name", "Brand", "Supplier", "Unit", "Price/Unit", "Pkg Price", "Pkg Size", "SKU"]];
  const body = items.map((i) => [
    i.name || "",
    i.brand_name || "-",
    i.supplier_name || "-",
    i.unit || "",
    fmt(i.price_per_unit, 4),
    i.package_price ? fmt(i.package_price) : "-",
    i.package_size ? fmt(i.package_size) : "-",
    i.sku || "-",
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head,
    body,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [97, 97, 97], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
  });

  addFooter(doc);
  openPdf(doc);
}

// ── Packaging Detail PDF ──────────────────────────────────────────────────

/**
 * Generate a detail PDF for a single packaging item.
 * @param {Object} item - Packaging object with all fields
 */
export async function exportPackagingDetailPdf(item) {
  if (!item) return;

  const doc = await createDoc();
  let y = addHeader(doc, item.name || "Packaging Item");

  // Fields
  y = printField(doc, "Brand", item.brand_name, y);
  y = printField(doc, "Supplier", item.supplier_name, y);
  y = printField(doc, "Unit", item.unit, y);
  y = printField(doc, "Price per Unit", fmt(item.price_per_unit, 4), y);
  if (item.package_price) y = printField(doc, "Package Price", fmt(item.package_price), y);
  if (item.package_size) y = printField(doc, "Package Size", fmt(item.package_size), y);
  if (item.sku) y = printField(doc, "SKU", item.sku, y);
  y = printField(doc, "Active", item.is_active ? "Yes" : "No", y);

  addFooter(doc);
  openPdf(doc);
}

// ── Combo-specific food cost ─────────────────────────────────────────────

/**
 * Calculate packaging cost for a specific platform/brand/location combo.
 * A packaging line matches if its dimension_id is NULL (applies to all) or
 * equals the combo's value.
 * @param {Array} packaging - Packaging lines array (each with platform_id, brand_id, location_id, line_cost)
 * @param {number|null} platformId - Selling price platform_id
 * @param {number|null} brandId - Selling price brand_id
 * @param {number|null} locationId - Selling price location_id
 * @returns {number} Packaging cost for this combo
 */
function comboPackagingCost(packaging, platformId, brandId, locationId) {
  if (!packaging?.length) return 0;
  return packaging
    .filter((p) => {
      // NULL on the packaging line means "applies to all";
      // NULL on the selling price side also means "match all"
      const matchPlatform = !p.platform_id || !platformId || p.platform_id === platformId;
      const matchBrand = !p.brand_id || !brandId || p.brand_id === brandId;
      const matchLocation = !p.location_id || !locationId || p.location_id === locationId;
      return matchPlatform && matchBrand && matchLocation;
    })
    .reduce((sum, p) => sum + Number(p.line_cost || 0), 0);
}

/**
 * Calculate food cost for a specific combo: recipe cost + combo packaging cost.
 * @param {Object} item - Menu item with recipe_cost and packaging array
 * @param {Object} sp - Selling price with platform_id, brand_id, location_id
 * @returns {number} Total food cost for this combo
 */
function comboFoodCost(item, sp) {
  const recipeCost = Number(item.recipe_cost || 0);
  const pkgCost = comboPackagingCost(item.packaging, sp.platform_id, sp.brand_id, sp.location_id);
  return recipeCost + pkgCost;
}

/**
 * Expand selling prices into per-brand rows when a selling price has no brand
 * but packaging lines have brand-specific costs.
 * E.g., one selling price (brand=null, price=61) with packaging for Felice and Little Italy
 * becomes two rows: one for Felice packaging cost, one for Little Italy packaging cost.
 * @param {Array} prices - Selling prices array
 * @param {Array} packaging - Packaging lines array
 * @returns {Array} Expanded selling prices (may be larger than input)
 */
function expandSellingPrices(prices, packaging) {
  if (!packaging?.length) return prices;
  const expanded = [];
  for (const sp of prices) {
    // If selling price already has a brand, keep as-is
    if (sp.brand_id) {
      expanded.push(sp);
      continue;
    }
    // Collect distinct brands from packaging lines (excluding null-brand lines)
    const pkgBrands = new Map();
    for (const p of packaging) {
      if (p.brand_id && !pkgBrands.has(p.brand_id)) {
        pkgBrands.set(p.brand_id, p.brand_name);
      }
    }
    // If packaging has brand-specific lines, expand into one row per brand
    if (pkgBrands.size > 1) {
      for (const [brandId, brandName] of pkgBrands) {
        expanded.push({ ...sp, brand_id: brandId, brand_name: brandName });
      }
    } else {
      expanded.push(sp);
    }
  }
  return expanded;
}

// ── Menu Items List PDF ──────────────────────────────────────────────────

/**
 * Generate a landscape PDF table listing all menu items.
 * Each menu item spans multiple rows — one per selling price combination
 * (platform/brand/location), with food cost recalculated per combo
 * (recipe cost + matching packaging lines).
 * Items with no selling prices get a single row with total food cost.
 * The item name is only shown on the first row of each group.
 *
 * @param {Array} items - Array of menu item objects (each with selling_prices and packaging arrays from detail fetch)
 */
export async function exportMenuItemsListPdf(items) {
  if (!items?.length) return;

  const doc = await createDoc(true);
  let y = addHeader(doc, "Menu Items");

  // Group items by category
  const grouped = {};
  for (const item of items) {
    const cat = item.category_name || "Uncategorized";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  }

  const head = [["Name", "Size", "Platform", "Brand", "Location", "Food Cost", "Price", "FC%"]];
  const body = [];

  // Build table body with category separator rows
  for (const category of Object.keys(grouped)) {
    // Category header row spanning all columns
    body.push([{ content: category, colSpan: 8, styles: { fontStyle: "bold", fillColor: [230, 230, 230], fontSize: 8 } }]);

    for (const item of grouped[category]) {
      // Expand selling prices: if a price has no brand but packaging has brand-specific
      // lines, split into one row per packaging brand to show per-brand food cost
      const prices = expandSellingPrices(item.selling_prices || [], item.packaging);
      if (prices.length === 0) {
        // Single row for items with no selling prices
        body.push([
          item.name || "",
          item.size || "-",
          "-", "-", "-",
          fmt(item.food_cost),
          "-", "-",
        ]);
      } else {
        // One row per selling price — recalculate food cost per combo
        prices.forEach((sp, idx) => {
          const fc = comboFoodCost(item, sp);
          const fcPct = sp.price && fc
            ? ((fc / sp.price) * 100).toFixed(1) + "%"
            : "-";
          body.push([
            idx === 0 ? (item.name || "") : "",
            idx === 0 ? (item.size || "-") : "",
            sp.platform_name || "All",
            sp.brand_name || "All",
            sp.location_name || "All",
            fmt(fc),
            fmt(sp.price),
            fcPct,
          ]);
        });
      }
    }
  }

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head,
    body,
    theme: "grid",
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [97, 97, 97], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7 },
    columnStyles: {
      5: { halign: "right" },  // Food Cost
      6: { halign: "right" },  // Price
      7: { halign: "right" },  // FC%
    },
  });

  addFooter(doc);
  openPdf(doc);
}

